import { useState } from 'react'
import PropTypes from 'prop-types'
import { Bar } from 'react-chartjs-2'
import { Chart as ChartJS, registerables } from 'chart.js'
import { Typography } from '@mui/material'
import { Modal } from 'frame/components'
import { themeV5 } from 'theme/v5'

ChartJS.register(...registerables)

const BarChart = ({ data, onClick, noDataMessage, showHealthAndSafetyDisclaimer }) => {
  const [openDisclaimerMessage, setOpenDisclaimerMessage] = useState(false)

  const getGraphData = () => ({
    labels: data.labels,
    datasets: [
      {
        label: 'This year',
        data: data.values,
        backgroundColor: data.colors,
        barThickness: 32,
        maxBarThickness: 38,
        barPercentage: 0.5,
        categoryPercentage: 0.5,
      }
    ]
  })

  const graph = getGraphData()
  const defaultTickColor = themeV5.palette.graph.secondary
  const overdueTickColor = themeV5.palette.brand.red

  const isOverdueLabel = (label) => {
    const labels = Array.isArray(label) ? label : [label]
    return labels.some((text) => {
      if (typeof text !== 'string') {
        return false
      }
      const lower = text.trim().toLowerCase()
      return lower === 'expired' || lower === 'overdue' || lower === 'matured'
    })
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: 0
    },
    scales: {
      x: {
        border: { display: false },
        grid: {
          display: false,
        },
        ticks: {
          padding: 20,
          color: (context) => isOverdueLabel(context.tick?.label) ? overdueTickColor : defaultTickColor,
        }
      },
      y: {
        border: { display: false },
        grid: {
          borderDash: [2],
          color: themeV5.palette.divider,
        },
        min: 0,
        ticks: {
          padding: 20,
          color: defaultTickColor,
          maxTicksLimit: 5,
          precision: 0,
        }
      }
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (tooltipItem) => {
            return ' ' + Math.round(tooltipItem.parsed.y * 100) / 100
          },
        }
      }
    },
    onClick,
  }

  const dataAvailable = data.values.length > 1

  return (
    <>
      {dataAvailable ? (
        <Bar
          height={130}
          data={graph}
          options={options}/>
      ) : (
        <Typography variant="body1">
          {noDataMessage ? noDataMessage : 'No data available'}

          {showHealthAndSafetyDisclaimer && (
            <>
              <br />
              <span
                style={{
                  color: themeV5.palette.text.link,
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
                onClick={() => setOpenDisclaimerMessage(true)}>
                  View Health and Safety Disclaimer
              </span>{'.'}
            </>
          )}
        </Typography>
      )}

      {openDisclaimerMessage && (
        <Modal
          open={openDisclaimerMessage}
          title="Health and Safety Disclaimer"
          onClose={() => setOpenDisclaimerMessage(false)}>
          Please note there are possible implications to your obligations both under your finance contract/lease and the Health
            and Safety at Work Act 2015 if the servicing requirements are not completed when due.
        </Modal>
      )}
    </>
  )
}

BarChart.propTypes = {
  data: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  noDataMessage: PropTypes.string,
  showHealthAndSafetyDisclaimer: PropTypes.bool,
}

export default BarChart
